import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getAuditTrailsByAdminId } from '@components/Api.jsx';

const AdminAuditTrailModal = ({ adminId, onClose }) => {
  const [auditTrails, setAuditTrails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [size] = useState(10); // Items per page

  const safeJsonDisplay = (jsonString) => {
    if (!jsonString) return 'N/A';
    try {
      // Check if it's already a valid JSON string
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If it's not valid JSON, return as-is
      return jsonString;
    }
  };

  useEffect(() => {
    fetchAuditTrails();
  }, [adminId, currentPage]);

  const fetchAuditTrails = async () => {
    try {
      setLoading(true);
      const data = await getAuditTrailsByAdminId(adminId, currentPage, size);
      
      if (data.content) {
        setAuditTrails(data.content);
        setTotalPages(data.totalPages);
        setTotalElements(data.totalElements);
      } else {
        setAuditTrails(data);
      }
    } catch (err) {
      setError(err.message || 'Failed to load audit trails');
      console.error('Error fetching audit trails:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'ADMIN_USER_CREATION':
        return 'bg-green-100 text-green-800';
      case 'ADMIN_USER_UPDATE':
        return 'bg-blue-100 text-blue-800';
      case 'ADMIN_USER_DEACTIVATION':
        return 'bg-red-100 text-red-800';
      case 'ADMIN_USER_REACTIVATION':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSuccessBadge = (isSuccessful) => {
    if (isSuccessful === null || isSuccessful === undefined) return null;
    return isSuccessful ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Success
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Failed
      </span>
    );
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-5xl w-full h-[80vh] flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">Admin Audit Trails</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="p-6 flex justify-center items-center flex-1">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-5xl w-full h-[80vh] flex flex-col overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
            <h2 className="text-xl font-semibold text-gray-900">Admin Audit Trails</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="p-6 flex-1 overflow-y-auto">
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-700 text-sm">Error loading audit trails: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-5xl w-full h-[80vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">Admin Audit Trails</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 flex-1 overflow-y-auto">
          <div className="mb-4 text-sm text-gray-600">
            Total records: {totalElements}
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entity Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditTrails.map((trail) => (
                  <tr key={trail.auditId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(trail.timestamp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionTypeColor(trail.actionType)}`}>
                        {trail.actionType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trail.entityType || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                      <div className="tooltip" title={trail.actionDescription}>
                        {trail.actionDescription}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trail.createdByAdminName || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getSuccessBadge(trail.successful)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {trail.ipAddress || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {Math.min(currentPage * size + 1, totalElements)} to {Math.min((currentPage + 1) * size, totalElements)} of {totalElements} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm bg-blue-600 text-white rounded">
                  {currentPage + 1} of {totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages - 1}
                  className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {/* Expandable details for each audit trail */}
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Detailed Logs</h3>
            {auditTrails.map((trail) => (
              <div key={trail.auditId} className="border border-gray-200 rounded-lg overflow-hidden">
                <details className="group">
                  <summary className="flex justify-between items-center p-4 bg-gray-50 cursor-pointer hover:bg-gray-100">
                    <div className="flex items-center space-x-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionTypeColor(trail.actionType)}`}>
                        {trail.actionType}
                      </span>
                      <span className="text-sm text-gray-900">{formatDate(trail.timestamp)}</span>
                      <span className="text-sm text-gray-900">by {trail.createdByAdminName || 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getSuccessBadge(trail.successful)}
                      <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </summary>
                  <div className="p-4 bg-white border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900">
                      <div>
                        <strong>Entity Type:</strong> {trail.entityType || 'N/A'}
                      </div>
                      <div>
                        <strong>Entity ID:</strong> {trail.entityId || 'N/A'}
                      </div>
                      <div>
                        <strong>Admin:</strong> {trail.adminName || 'N/A'}
                      </div>
                      <div>
                        <strong>Created By:</strong> {trail.createdByAdminName || 'N/A'}
                      </div>
                      <div>
                        <strong>IP Address:</strong> {trail.ipAddress || 'N/A'}
                      </div>
                      <div>
                        <strong>User Agent:</strong> {trail.userAgent || 'N/A'}
                      </div>
                    </div>
                    
                    {(trail.beforeValues || trail.afterValues) && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-2">Changes</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {trail.beforeValues && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-1">Before</h5>
                              <pre className="bg-gray-50 p-2 rounded text-xs text-gray-900 overflow-x-auto max-h-32 overflow-y-auto">
                                {safeJsonDisplay(trail.beforeValues)}
                              </pre>
                            </div>
                          )}
                          {trail.afterValues && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-900 mb-1">After</h5>
                              <pre className="bg-gray-50 p-2 rounded text-xs text-gray-900 overflow-x-auto max-h-32 overflow-y-auto">
                                {safeJsonDisplay(trail.afterValues)}
                              </pre>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditTrailModal;